import {
  ArrowRight,
  Baby,
  Check,
  ChevronDown,
  CircleDollarSign,
  Dna,
  Egg,
  Globe2,
  HeartPulse,
  Menu,
  Microscope,
  Minus,
  Plus,
  Scale,
  ShieldCheck,
  Snowflake,
  Stethoscope,
  X
} from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion, useScroll, useSpring } from 'framer-motion';
import { FormEvent, ReactNode, RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { submitConsultation } from '../api';
import {
  getGlobalContent,
  getLocaleFromPath,
  localeMeta,
  routeForLocale,
  type GlobalLocale,
  type GlobalSafeContent,
  type SafeFact
} from './content';

const navigationTargets = ['facts', 'pathways', 'vetting', 'countries', 'guidance', 'consultation'];
const serviceIcons = [Microscope, Egg, Baby, Snowflake, Scale, Stethoscope];
const pathwayImages = ['/global/pathway-ivf.jpg', '/global/pathway-donor.jpg', '/global/pathway-surrogacy.jpg'];
const pathwayMeters = [
  { timeline: 2, legal: 1, cost: 2 },
  { timeline: 3, legal: 3, cost: 3 },
  { timeline: 5, legal: 5, cost: 5 }
];

function useFocusTrap(
  active: boolean,
  containerRef: RefObject<HTMLElement | null>,
  returnRef: RefObject<HTMLElement | null>,
  onClose: () => void
) {
  useEffect(() => {
    if (!active || !containerRef.current) return;
    const container = containerRef.current;
    const selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusable = Array.from(container.querySelectorAll<HTMLElement>(selector));
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.requestAnimationFrame(() => first?.focus());

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || focusable.length === 0) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      window.requestAnimationFrame(() => returnRef.current?.focus());
    };
  }, [active, containerRef, onClose, returnRef]);
}

function Reveal({ children, className = '', delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  const reducedMotion = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reducedMotion ? false : { opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.18 }}
      transition={{ duration: reducedMotion ? 0 : 0.78, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}

function SectionLabel({ index, children }: { index?: string; children: ReactNode }) {
  return (
    <div className="global-section-label">
      {index ? <span>{index}</span> : null}
      <i aria-hidden="true" />
      <strong>{children}</strong>
    </div>
  );
}

function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 90, damping: 30, mass: 0.4 });
  return <motion.div className="global-scroll-progress" style={{ scaleX }} aria-hidden="true" />;
}

function GlobalHeader({ locale, content }: { locale: GlobalLocale; content: GlobalSafeContent }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const languageRef = useRef<HTMLDivElement>(null);

  useFocusTrap(menuOpen, menuRef, menuTriggerRef, () => setMenuOpen(false));

  useEffect(() => {
    if (!languageOpen) return;
    const close = (event: PointerEvent) => {
      if (!languageRef.current?.contains(event.target as Node)) setLanguageOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [languageOpen]);

  const switchLocale = (nextLocale: GlobalLocale) => {
    window.location.assign(routeForLocale(nextLocale));
  };

  return (
    <>
      <header className="global-header">
        <a className="global-brand" href={routeForLocale(locale, '')} aria-label="IVFToday home">
          <img src="/brand/ivftoday-logo-640.png" alt="IVFToday" width="640" height="270" />
        </a>
        <div className="global-header-actions">
          <div className="global-language" ref={languageRef}>
            <button
              type="button"
              aria-label={content.utility.language}
              aria-expanded={languageOpen}
              onClick={() => setLanguageOpen((current) => !current)}
            >
              <Globe2 aria-hidden="true" />
              <span>{localeMeta[locale].short}</span>
              <ChevronDown className={languageOpen ? 'rotated' : ''} aria-hidden="true" />
            </button>
            <AnimatePresence>
              {languageOpen ? (
                <motion.div
                  className="global-language-menu"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  role="menu"
                >
                  {(Object.keys(localeMeta) as GlobalLocale[]).map((option) => (
                    <button key={option} type="button" role="menuitem" onClick={() => switchLocale(option)}>
                      <span>{localeMeta[option].label}</span>
                      {option === locale ? <Check aria-hidden="true" /> : null}
                    </button>
                  ))}
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
          <a className="global-header-book" href="#consultation">{content.nav.book}</a>
          <button
            ref={menuTriggerRef}
            className="global-menu-trigger"
            type="button"
            aria-label={content.nav.menu}
            onClick={() => setMenuOpen(true)}
            aria-expanded={menuOpen}
            aria-controls="global-curtain-menu"
          >
            <span>{content.nav.menu}</span>
            <Menu aria-hidden="true" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen ? (
          <motion.div
            ref={menuRef}
            id="global-curtain-menu"
            className="global-curtain"
            role="dialog"
            aria-modal="true"
            aria-label={content.nav.menu}
            initial={{ y: '-100%' }}
            animate={{ y: 0 }}
            exit={{ y: '-100%' }}
            transition={{ duration: 0.62, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="global-curtain-head">
              <span className="global-brand global-brand-light"><img src="/brand/ivftoday-logo-640.png" alt="IVFToday" width="640" height="270" /></span>
              <button type="button" onClick={() => setMenuOpen(false)} aria-label={content.utility.close}><X aria-hidden="true" /></button>
            </div>
            <div className="global-curtain-body">
              <nav aria-label={content.nav.menu}>
                {content.nav.links.map((label, index) => (
                  <motion.a
                    key={navigationTargets[index]}
                    href={`#${navigationTargets[index]}`}
                    onClick={() => setMenuOpen(false)}
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.12 + index * 0.05 }}
                  >
                    {label}
                  </motion.a>
                ))}
              </nav>
              <aside>
                <p>{content.nav.quickStats}</p>
                {content.nav.stats.map(([value, label]) => (
                  <div key={value}>
                    <strong>{value}</strong>
                    <span>{label}</span>
                  </div>
                ))}
              </aside>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  );
}

function Hero({ locale, content }: { locale: GlobalLocale; content: GlobalSafeContent }) {
  const alt = {
    en: 'Fertility doctor in a bright modern clinic',
    ko: '밝고 현대적인 병원의 생식 의료진',
    ja: '明るく現代的なクリニックの生殖医療スタッフ',
    zh: '明亮现代诊所中的生殖医疗人员'
  }[locale];
  return (
    <section id="top" className="global-hero">
      <div className="global-hero-image">
        <img src="/global/hero-clinic.jpg" alt={alt} width="1024" height="1024" fetchPriority="high" />
      </div>
      <div className="global-hero-copy">
        <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
          <p className="global-eyebrow">{content.hero.eyebrow}</p>
          <h1>
            {content.hero.title1}<br />
            {content.hero.title2}<br />
            <em>{content.hero.title3}</em>
          </h1>
          <p className="global-hero-subtitle">{content.hero.subtitle}</p>
          <div className="global-hero-actions">
            <a className="global-primary-button" href="#consultation">{content.hero.cta}<ArrowRight aria-hidden="true" /></a>
            <a className="global-text-link" href="#pathways">{content.hero.explore}<ArrowRight aria-hidden="true" /></a>
          </div>
        </motion.div>
      </div>
      <motion.span className="global-scroll-dot" animate={{ opacity: [0.3, 1, 0.3], scale: [1, 1.6, 1] }} transition={{ duration: 2.5, repeat: Infinity }} aria-hidden="true" />
    </section>
  );
}

function Services({ locale, content }: { locale: GlobalLocale; content: GlobalSafeContent }) {
  const alt = {
    en: 'Bright reception area in a modern fertility clinic', ko: '현대적인 생식 클리닉의 밝은 접수 공간',
    ja: '現代的な生殖医療クリニックの明るい受付', zh: '现代生殖诊所的明亮接待区'
  }[locale];
  return (
    <section id="services" className="global-services global-section-band">
      <div className="global-section-intro global-services-intro">
        <Reveal>
          <SectionLabel>{content.services.label}</SectionLabel>
          <h2>{content.services.heading}</h2>
          <p>{content.services.intro}</p>
        </Reveal>
        <Reveal className="global-services-image" delay={0.08}>
          <img src="/global/services-clinic.jpg" alt={alt} width="1024" height="1024" loading="lazy" />
        </Reveal>
      </div>
      <div className="global-service-grid">
        {content.services.items.map((item, index) => {
          const Icon = serviceIcons[index];
          return (
            <Reveal key={item.title} delay={index * 0.04}>
              <a href="#pathways" className="global-service-card">
                <span><Icon aria-hidden="true" /></span>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
                <small>{content.services.learnMore}<ArrowRight aria-hidden="true" /></small>
              </a>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

function AnimatedFactValue({ fact }: { fact: SafeFact }) {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (!fact.countTo || reducedMotion) {
      setValue(fact.countTo ?? 0);
      return;
    }
    const element = ref.current;
    if (!element) return;
    let frame = 0;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      const start = performance.now();
      const tick = (now: number) => {
        const progress = Math.min((now - start) / 1400, 1);
        setValue(fact.countTo! * (1 - Math.pow(1 - progress, 3)));
        if (progress < 1) frame = window.requestAnimationFrame(tick);
      };
      frame = window.requestAnimationFrame(tick);
    }, { threshold: 0.5 });
    observer.observe(element);
    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(frame);
    };
  }, [fact.countTo, reducedMotion]);

  if (!fact.countTo) return <span>{fact.value}</span>;
  return <span ref={ref}>{Math.round(value)}{fact.suffix}</span>;
}

function Facts({ content }: { content: GlobalSafeContent }) {
  return (
    <section id="facts" className="global-section global-facts">
      <Reveal className="global-facts-lead">
        <SectionLabel index="01">{content.facts.label}</SectionLabel>
        <h2>{content.facts.heading}</h2>
        <p>{content.facts.intro}</p>
      </Reveal>
      <div className="global-facts-grid">
        {content.safeFacts.map((fact, index) => (
          <Reveal key={fact.label} delay={index * 0.07}>
            <article className="global-fact">
              <AnimatedFactValue fact={fact} />
              <i aria-hidden="true" />
              <h3>{fact.label}</h3>
              <p>{fact.note}</p>
              {fact.source ? <a href={fact.source.href} target="_blank" rel="noreferrer">{fact.source.label}<ArrowRight aria-hidden="true" /></a> : null}
            </article>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Meter({ label, value }: { label: string; value: number }) {
  return (
    <div className="global-meter">
      <span>{label}</span>
      <i>{[1, 2, 3, 4, 5].map((number) => <b className={number <= value ? 'filled' : ''} key={number} />)}</i>
    </div>
  );
}

function Pathways({ locale, content }: { locale: GlobalLocale; content: GlobalSafeContent }) {
  const [active, setActive] = useState(0);
  const imageAlt = {
    en: ['A couple sitting together in warm daylight', 'A cradle in soft natural light', 'A family embracing outdoors'],
    ko: ['따뜻한 햇살 속에 함께 앉은 커플', '부드러운 자연광 속의 아기 침대', '야외에서 포옹하는 가족'],
    ja: ['暖かな光の中で寄り添うカップル', '柔らかな自然光のベビーベッド', '屋外で抱き合う家族'],
    zh: ['暖光中相伴而坐的伴侣', '柔和自然光中的婴儿床', '户外拥抱的家庭']
  }[locale];
  return (
    <section id="pathways" className="global-section global-pathways">
      <Reveal className="global-two-column-heading">
        <div>
          <SectionLabel index="02">{content.pathways.label}</SectionLabel>
          <h2>{content.pathways.heading}</h2>
        </div>
        <p>{content.pathways.intro}</p>
      </Reveal>
      <div className="global-pathway-row">
        {content.pathways.items.map((pathway, index) => {
          const selected = active === index;
          return (
            <motion.article
              className={`global-pathway-card ${selected ? 'active' : ''}`}
              key={pathway.title}
              onMouseEnter={() => setActive(index)}
              onFocus={() => setActive(index)}
              onClick={() => setActive(index)}
              tabIndex={0}
              aria-expanded={selected}
              layout
            >
              <img src={pathwayImages[index]} alt={imageAlt[index]} width="896" height="1200" loading="lazy" />
              <div className="global-pathway-shade" aria-hidden="true" />
              <div className="global-pathway-copy">
                <small>{pathway.tag}</small>
                <h3>{pathway.title}</h3>
                <p>{pathway.summary}</p>
                <AnimatePresence initial={false}>
                  {selected ? (
                    <motion.div className="global-pathway-details" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
                      <ol>{pathway.steps.map((step, stepIndex) => <li key={step}><span>/ {String(stepIndex + 1).padStart(2, '0')}</span>{step}</li>)}</ol>
                      <Meter label={content.pathways.meters.timeline} value={pathwayMeters[index].timeline} />
                      <Meter label={content.pathways.meters.legal} value={pathwayMeters[index].legal} />
                      <Meter label={content.pathways.meters.cost} value={pathwayMeters[index].cost} />
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </div>
            </motion.article>
          );
        })}
      </div>
    </section>
  );
}

function Vetting({ content }: { content: GlobalSafeContent }) {
  return (
    <section id="vetting" className="global-vetting">
      <div className="global-vetting-lead">
        <Reveal>
          <SectionLabel index="03">{content.vetting.label}</SectionLabel>
          <h2>{content.vetting.heading}</h2>
          <p>{content.vetting.intro}</p>
        </Reveal>
      </div>
      <div className="global-vetting-list">
        {content.vetting.items.map((item, index) => (
          <Reveal key={item.t} delay={index * 0.04}>
            <article><span>{String(index + 1).padStart(2, '0')}</span><div><h3>{item.t}</h3><p>{item.d}</p></div></article>
          </Reveal>
        ))}
        <p className="global-vetting-note"><ShieldCheck aria-hidden="true" />{content.vetting.note}</p>
      </div>
    </section>
  );
}

type Country = GlobalSafeContent['ecosystems']['countries'][number];

function CountryPanel({ country, content, onClose, returnRef }: { country: Country | null; content: GlobalSafeContent; onClose: () => void; returnRef: RefObject<HTMLElement | null> }) {
  const panelRef = useRef<HTMLElement>(null);
  useFocusTrap(Boolean(country), panelRef, returnRef, onClose);
  return (
    <AnimatePresence>
      {country ? (
        <>
          <motion.button className="global-panel-overlay" aria-label={content.utility.close} onClick={onClose} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />
          <motion.aside
            ref={panelRef}
            className="global-country-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="country-panel-title"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="global-country-panel-head">
              <div><small>{country.stat}</small><h3 id="country-panel-title">{country.name}</h3><p>{country.hub}</p></div>
              <button type="button" onClick={onClose} aria-label={content.utility.close}><X aria-hidden="true" /></button>
            </div>
            <i aria-hidden="true" />
            <h4>{country.headline}</h4>
            <p>{country.pathways}</p>
            <small>{content.ecosystems.checklistLabel}</small>
            <ul>{country.checklist.map((item) => <li key={item}><span>/</span>{item}</li>)}</ul>
            <a className="global-primary-button" href="#consultation" onClick={onClose}>{content.ecosystems.discuss} {country.name}<ArrowRight aria-hidden="true" /></a>
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>
  );
}

function Ecosystems({ content }: { content: GlobalSafeContent }) {
  const [country, setCountry] = useState<Country | null>(null);
  const lastTrigger = useRef<HTMLElement>(null);
  const selectCountry = (selected: Country, trigger: HTMLElement) => {
    lastTrigger.current = trigger;
    setCountry(selected);
  };
  return (
    <section id="countries" className="global-section global-ecosystems">
      <Reveal className="global-two-column-heading">
        <div><SectionLabel index="04">{content.ecosystems.label}</SectionLabel><h2>{content.ecosystems.heading}</h2></div>
        <p>{content.ecosystems.intro}</p>
      </Reveal>
      <div className="global-ecosystem-grid">
        <Reveal>
          <div className="global-map">
            <img src="/global/ecosystem-map.jpg" alt="" width="1376" height="768" loading="lazy" />
            {content.ecosystems.countries.map((item) => (
              <button
                key={item.id}
                type="button"
                style={{ left: `${item.x}%`, top: `${item.y}%` }}
                onClick={(event) => selectCountry(item, event.currentTarget)}
              >
                <i aria-hidden="true" /><span>{item.name}</span>
              </button>
            ))}
          </div>
          <p className="global-map-caption">{content.ecosystems.caption}</p>
        </Reveal>
        <div className="global-country-list">
          {content.ecosystems.countries.map((item, index) => (
            <Reveal key={item.id} delay={index * 0.035}>
              <button type="button" onClick={(event) => selectCountry(item, event.currentTarget)}>
                <span><strong>{item.name}</strong><small>{item.pathways}</small></span>
                <em>{item.stat}</em>
              </button>
            </Reveal>
          ))}
        </div>
      </div>
      <CountryPanel country={country} content={content} onClose={() => setCountry(null)} returnRef={lastTrigger} />
    </section>
  );
}

function Process({ locale, content }: { locale: GlobalLocale; content: GlobalSafeContent }) {
  const alt = {
    en: 'Modern fertility clinic exterior in daylight', ko: '낮에 본 현대적인 생식 클리닉 건물',
    ja: '昼間の現代的な生殖医療クリニック外観', zh: '日光下的现代生殖诊所建筑'
  }[locale];
  return (
    <section id="guidance" className="global-section global-process">
      <Reveal className="global-two-column-heading">
        <div><SectionLabel index="05">{content.process.label}</SectionLabel><h2>{content.process.heading}</h2></div>
        <p>{content.process.intro}</p>
      </Reveal>
      <div className="global-process-grid">
        <div className="global-process-steps">
          {content.process.steps.map((step, index) => (
            <Reveal key={step.t} delay={index * 0.04}>
              <article><span>/ {String(index + 1).padStart(2, '0')}</span><div><h3>{step.t}</h3><p>{step.d}</p></div></article>
            </Reveal>
          ))}
        </div>
        <Reveal className="global-process-media" delay={0.08}>
          <img src="/global/guidance-clinic.jpg" alt={alt} width="1024" height="1024" loading="lazy" />
          <blockquote>{content.process.quote}</blockquote>
          <cite>{content.process.quoteAttribution}</cite>
        </Reveal>
      </div>
    </section>
  );
}

function Trust({ content }: { content: GlobalSafeContent }) {
  return (
    <section className="global-section global-trust">
      <Reveal>
        <SectionLabel index="06">{content.trust.label}</SectionLabel>
        <h2>{content.trust.heading}</h2>
      </Reveal>
      <div className="global-trust-grid">
        {content.trust.items.map((item, index) => (
          <Reveal key={item.title} delay={index * 0.08}>
            <article><span>{String(index + 1).padStart(2, '0')}</span><h3>{item.title}</h3><p>{item.detail}</p></article>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function FAQ({ content }: { content: GlobalSafeContent }) {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="global-section global-faq">
      <Reveal className="global-two-column-heading">
        <div><SectionLabel index="07">{content.faq.label}</SectionLabel><h2>{content.faq.heading}</h2></div>
        <p>{content.faq.intro}</p>
      </Reveal>
      <div className="global-faq-list">
        {content.faq.items.map((item, index) => {
          const expanded = open === index;
          return (
            <Reveal key={item.q} delay={index * 0.03}>
              <article>
                <h3>
                  <button
                    type="button"
                    aria-expanded={expanded}
                    aria-controls={`faq-panel-${index}`}
                    onClick={() => setOpen(expanded ? -1 : index)}
                  >
                    <span>{item.q}</span>{expanded ? <Minus aria-hidden="true" /> : <Plus aria-hidden="true" />}
                  </button>
                </h3>
                <AnimatePresence initial={false}>
                  {expanded ? (
                    <motion.div id={`faq-panel-${index}`} initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                      <p>{item.a}</p>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </article>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

interface ConsultationFormState {
  consultationDate: string;
  pathwayInterest: string;
  fullName: string;
  email: string;
  phone: string;
  countryInterest: string;
  notes: string;
  consent: boolean;
}

function consultationDays(locale: GlobalLocale) {
  const formatter = new Intl.DateTimeFormat(localeMeta[locale].lang, { weekday: 'short', month: 'short', day: 'numeric' });
  const days: Array<{ value: string; label: string }> = [];
  const date = new Date();
  while (days.length < 8) {
    date.setDate(date.getDate() + 1);
    if (date.getDay() === 0) continue;
    days.push({ value: date.toISOString().slice(0, 10), label: formatter.format(date) });
  }
  return days;
}

function Consultation({ locale, content }: { locale: GlobalLocale; content: GlobalSafeContent }) {
  const days = useMemo(() => consultationDays(locale), [locale]);
  const [form, setForm] = useState<ConsultationFormState>({
    consultationDate: '', pathwayInterest: 'unsure', fullName: '', email: '', phone: '', countryInterest: '', notes: '', consent: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [reference, setReference] = useState('');

  const update = <K extends keyof ConsultationFormState>(key: K, value: ConsultationFormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: '' }));
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === 'sending') return;
    const nextErrors: Record<string, string> = {};
    if (!form.consultationDate) nextErrors.consultationDate = content.utility.dateError;
    if (!form.fullName.trim()) nextErrors.fullName = content.utility.nameError;
    if (!/^\S+@\S+\.\S+$/.test(form.email)) nextErrors.email = content.utility.emailError;
    if (!form.consent) nextErrors.consent = content.utility.consentError;
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setStatus('sending');
    const result = await submitConsultation({
      consultationDate: form.consultationDate,
      pathwayInterest: form.pathwayInterest,
      fullName: form.fullName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      countryInterest: form.countryInterest.trim(),
      notes: form.notes.trim(),
      sourceLocale: locale,
      sourceHost: window.location.hostname,
      sourceChannel: 'global-guidance',
      consentTimestamp: new Date().toISOString(),
      consentPolicyVersion: '2026-08-10'
    });
    if (!result.ok) {
      setStatus('error');
      return;
    }
    setReference(result.id ?? '');
    setStatus('success');
  };

  return (
    <section id="consultation" className="global-consultation">
      <Reveal className="global-consultation-heading">
        <SectionLabel index="08">{content.booking.label}</SectionLabel>
        <h2>{content.booking.heading}</h2>
        <p>{content.booking.intro}</p>
      </Reveal>
      <Reveal className="global-consultation-card" delay={0.08}>
        {status === 'success' ? (
          <div className="global-consultation-success" role="status">
            <span><Check aria-hidden="true" /></span>
            <h3>{content.booking.thanks}</h3>
            <p>{content.booking.thanksBody}</p>
            {reference ? <small>{content.utility.leadReference}: <b>{reference}</b></small> : null}
          </div>
        ) : (
          <form onSubmit={submit} noValidate>
            <fieldset>
              <legend>{content.booking.chooseDay}</legend>
              <div className="global-date-grid">
                {days.map((day) => (
                  <button
                    key={day.value}
                    type="button"
                    className={form.consultationDate === day.value ? 'selected' : ''}
                    aria-pressed={form.consultationDate === day.value}
                    onClick={() => update('consultationDate', day.value)}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
              {errors.consultationDate ? <p className="global-form-error">{errors.consultationDate}</p> : null}
            </fieldset>
            <fieldset>
              <legend>{content.booking.pathwayLabel}</legend>
              <div className="global-pathway-options">
                {content.booking.pathways.map((pathway) => (
                  <label key={pathway.v}>
                    <input type="radio" name="pathway" value={pathway.v} checked={form.pathwayInterest === pathway.v} onChange={() => update('pathwayInterest', pathway.v)} />
                    <span>{pathway.l}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="global-form-grid">
              <label>
                <span>{content.booking.fields.fullName} <small>{content.utility.required}</small></span>
                <input autoComplete="name" value={form.fullName} onChange={(event) => update('fullName', event.target.value)} aria-invalid={Boolean(errors.fullName)} />
                {errors.fullName ? <em>{errors.fullName}</em> : null}
              </label>
              <label>
                <span>{content.booking.fields.email} <small>{content.utility.required}</small></span>
                <input type="email" inputMode="email" autoComplete="email" value={form.email} onChange={(event) => update('email', event.target.value)} aria-invalid={Boolean(errors.email)} />
                {errors.email ? <em>{errors.email}</em> : null}
              </label>
              <label>
                <span>{content.booking.fields.phone}</span>
                <input type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={(event) => update('phone', event.target.value)} />
              </label>
              <label>
                <span>{content.booking.fields.country}</span>
                <input autoComplete="country-name" value={form.countryInterest} onChange={(event) => update('countryInterest', event.target.value)} placeholder={content.booking.undecided} />
              </label>
              <label className="global-form-wide">
                <span>{content.booking.fields.notes}</span>
                <textarea rows={4} value={form.notes} onChange={(event) => update('notes', event.target.value)} maxLength={1200} />
              </label>
            </div>
            <label className="global-consent">
              <input type="checkbox" checked={form.consent} onChange={(event) => update('consent', event.target.checked)} />
              <span>{content.utility.consent}</span>
            </label>
            {errors.consent ? <p className="global-form-error">{errors.consent}</p> : null}
            <p className="global-form-disclaimer">{content.utility.formDisclaimer}</p>
            {status === 'error' ? <p className="global-form-error" role="alert">{content.booking.error}</p> : null}
            <button className="global-primary-button global-submit" type="submit" disabled={status === 'sending'}>
              {status === 'sending' ? content.booking.sending : content.booking.submit}<ArrowRight aria-hidden="true" />
            </button>
            <small className="global-privacy"><ShieldCheck aria-hidden="true" />{content.utility.privacy}</small>
          </form>
        )}
      </Reveal>
    </section>
  );
}

function Footer({ locale, content }: { locale: GlobalLocale; content: GlobalSafeContent }) {
  return (
    <footer className="global-footer">
      <div className="global-footer-grid">
        <div><span className="global-brand"><img src="/brand/ivftoday-logo-640.png" alt="IVFToday" width="640" height="270" /></span><p>{content.footer.description}</p></div>
        <nav aria-label={content.footer.explore}>
          <strong>{content.footer.explore}</strong>
          {content.nav.links.slice(0, 5).map((label, index) => <a key={label} href={`#${navigationTargets[index]}`}>{label}</a>)}
        </nav>
        <div>
          <strong>{content.footer.contact}</strong>
          <a href="#consultation">{content.nav.book}</a>
          <span>{localeMeta[locale].label}</span>
        </div>
      </div>
      <div className="global-footer-bottom"><span>© {new Date().getFullYear()} IVFToday</span><span>{content.footer.disclaimer}</span></div>
    </footer>
  );
}

export default function GlobalLanding() {
  const locale = getLocaleFromPath();
  const content = useMemo(() => getGlobalContent(locale), [locale]);

  useEffect(() => {
    document.documentElement.lang = localeMeta[locale].lang;
    document.documentElement.dataset.site = 'global';
    return () => { delete document.documentElement.dataset.site; };
  }, [locale]);

  return (
    <div className={`global-site global-locale-${locale}`}>
      <a className="global-skip-link" href="#global-main">{content.utility.skip}</a>
      <ScrollProgress />
      <GlobalHeader locale={locale} content={content} />
      <main id="global-main">
        <Hero locale={locale} content={content} />
        <Services locale={locale} content={content} />
        <Facts content={content} />
        <Pathways locale={locale} content={content} />
        <Vetting content={content} />
        <Ecosystems content={content} />
        <Process locale={locale} content={content} />
        <Trust content={content} />
        <FAQ content={content} />
        <Consultation locale={locale} content={content} />
      </main>
      <Footer locale={locale} content={content} />
    </div>
  );
}
